import React, { useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Formik } from 'formik';

import Col from 'components/ui/Col';
import Row from 'components/ui/Row';

import { LeaseIncentiveList } from './LeaseIncentiveList';

import { StyledContainer } from '../common/LeaseStyles';

import { LeaseCreatorPageFooter } from '../common/LeaseCreatorPageFooter';
import { LeaseCreatorFormItem } from '../common/LeaseCreatorFormItem';
import { LeaseRejectedAlert } from '../common/LeaseRejectedAlert';

import { getTranslatedString } from 'helpers/i18n';
import { leaseCreatorColors } from 'helpers/ColorHelper';
import LeaseHeaderProgress from '../common/LeaseHeaderProgress';
import { responsive } from 'helpers/StyleHelper';
import { media, sizes } from 'helpers/MediaHelper';

const FORM_NAME = 'lease_incentives_page';

const StyledFormContainer = styled.div`
  background-color: white;
  width: 100%;
  margin-top: 24px;
  border: 1px solid ${leaseCreatorColors.FORM_BORDER};
  border-radius: 5px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  font-size: 24px;
  padding: 16px 16px 0px;
  max-width: 580px;
`;

const StyledContainerOuterForm = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  font-size: 24px;
  max-width: 580px;
  padding: 0 16px 16px 16px;
  ${media.mobile`
    padding: 16px;
  `};
`;

const StyledPageHeader = styled.div`
  font-size: 24px;
  line-height: 32px;
  color: ${leaseCreatorColors.HEADER};
  font-weight: 400;
  margin-bottom: 16px;
  ${media.mobile`
    margin-bottom: 0;
  `};
`;

const StyledHeaderDef = styled.div`
  font-size: 14px;
  line-height: 24px;
  color: ${leaseCreatorColors.HEADER};
  font-weight: 400;
  margin-bottom: 16px;
`;

const StyledRow = styled(Row)`
  width: 100%;
  margin-bottom: ${(props) => props.mb || '21px'};
  margin-top: ${(props) => props.mt || '0px'};
  ${media.mobile`
    margin-bottom: 0;
  `};
`;

const StyledRequiredText = styled.span`
  font-size: ${responsive.text.desktop};
  ${media.mobile`
    font-weight: 400;
    font-size: ${responsive.text.mobile};
  `};
`;

const StyledLeaseCreatorFormItem = styled(LeaseCreatorFormItem)`
  label {
    margin-bottom: 0;
  }
`;

const LeaseIncentivesPage = ({
  forward,
  back,
  currentPage,
  totalPages,
  leaseFormValues,
  setLeaseFormValues,
  leaseDetails,
  saveDraftLease,
  handleCancellation,
  latestLeaseReview,
}) => {
  const [validIncentives, setValidIncentives] = useState([]);

  const isMobile = window.matchMedia(`(max-width: ${sizes.mobile}px)`).matches;
  const labelSize = isMobile ? responsive.text.mobile : responsive.text.desktop;

  return (
    <StyledContainer>
      <LeaseHeaderProgress lease={leaseDetails} stepsFilled={currentPage} totalSteps={totalPages} />

      <StyledFormContainer>
        <Formik
          initialValues={{}}
          onSubmit={(values) => {
            values.lease_incentives = validIncentives;
            setLeaseFormValues((leaseFormValues) => ({
              ...leaseFormValues,
              ...values,
            }));
            saveDraftLease(values);
            forward();
          }}
        >
          {({ handleSubmit }) => {
            return (
              <StyledForm id={FORM_NAME} onSubmit={handleSubmit}>
                <StyledPageHeader>
                  {getTranslatedString(
                    'landlordPortal.leaseCreatorV2.leaseIncentivesPage.incentives'
                  )}
                </StyledPageHeader>
                {latestLeaseReview?.status === 'rejected' && (
                  <StyledRow gutter={16}>
                    <LeaseRejectedAlert latestLeaseReview={latestLeaseReview} />
                  </StyledRow>
                )}
              </StyledForm>
            );
          }}
        </Formik>

        <StyledContainerOuterForm>
          <StyledHeaderDef>
            {getTranslatedString(
              'landlordPortal.leaseCreatorV2.leaseIncentivesPage.includerentalIncentive'
            )}
          </StyledHeaderDef>

          <StyledRow gutter={16}>
            <Col span={24}>
              <LeaseIncentiveList
                selectedLeaseStartDate={leaseFormValues.lease_start_date} // needed to compare with start date of rentable items
                selectedEarlyMoveInDate={leaseFormValues.early_move_in_date} // needed to compare with start date of rentable items
                setValidIncentives={setValidIncentives}
                validIncentives={validIncentives}
                leaseDraftLeaseIncentives={leaseDetails.draft_lease_data?.lease_incentives}
              />
            </Col>
          </StyledRow>
          <StyledRow mb="0px">
            <Col span={24}>
              <StyledLeaseCreatorFormItem
                label={<StyledRequiredText>{getTranslatedString('required')}</StyledRequiredText>}
                labelSize={labelSize}
                required
              />
            </Col>
          </StyledRow>
        </StyledContainerOuterForm>
      </StyledFormContainer>
      <LeaseCreatorPageFooter
        back={back}
        currentPage={currentPage}
        totalPages={totalPages}
        formName={FORM_NAME}
        handleCancellation={handleCancellation}
      />
    </StyledContainer>
  );
};

const mapStoreToProps = (store) => ({
  latestLeaseReview: store.landlord.leaseCreatorV2.latestLeaseReview,
});

const mapDispatchToProps = {};

export default connect(mapStoreToProps, mapDispatchToProps)(LeaseIncentivesPage);
